/**
 * THE MASTER TOOL REGISTRY — every addressable capability, once, keyed by a stable tool id.
 *
 * <h2>What this replaces</h2>
 *
 * Four subtractive mechanisms decided what a toolbar showed: {@code PRODUCT_DENIES},
 * {@code PROFILE_DENIES}, relocation (with a per-product exemption table) and the short-bar
 * preset — plus a catalog mode that selectively bypassed three of the four. Each is correct in
 * isolation and together they answer "does this icon render" in four places, so a product gained
 * a control by nobody naming it. This is the ALLOW-LIST half: a manifest names tool ids, and an
 * id a manifest does not name is never mounted.
 *
 * <h2>⚠ THE ID IS NOT THE ACTION ID, and that separation is the point</h2>
 *
 * {@code actionId} is the string the dispatcher already routes — {@code open_controls},
 * {@code run_cycle_long}. Those names are historical, inconsistent and in places misleading
 * ({@code open_controls} is the Longitude Study). A manifest is read by a human deciding what a
 * product contains, so it reads {@code 'longitude_study'}.
 *
 * <p>Keeping them separate also means a manifest never has to change when a dispatcher case is
 * renamed, and two tools can share one action (the three CTS orbs do not, but the pattern is
 * available) without the manifest having to know.
 *
 * <h2>⚠ STAGE 1 BOUNDARY: THE ICONS ARE NOT HERE</h2>
 *
 * The brief's entry shape carries {@code icon}. The 48 declarative entries live in a 558-line
 * array inside {@code TabPanel.js} as inline SVG, referencing some sixty imports; moving them is
 * a mechanical change large enough to make everything else in this refactor unreviewable, and it
 * buys nothing a later stage cannot buy just as well.
 *
 * <p>So an entry may carry {@code icon} and most do not. The toolbar resolves a missing one from
 * the existing declaration BY {@code actionId}, which is why {@link actionIdsOf} exists. The
 * registry is the single master dictionary of TOOLS; it is not yet the dictionary of pixels.
 *
 * <h2>Categories</h2>
 *
 * {@code ephemeris} planetary and astrological studies · {@code cycle} the timing engines, scans
 * and confluence work · {@code geometric} price-and-time projections · {@code drawing} the pens ·
 * {@code indicator} the technical overlays · {@code utility} chrome, exports and diagnostics.
 *
 * <p>The category is for GROUPING a manifest's list and for the Admin Panel's tabs. It is not a
 * permission and nothing gates on it: a manifest names ids, never a category, so "every ephemeris
 * tool" can never silently acquire a new member.
 */

/**
 * ⚠ `requiresLBC` IS A GUARD, NOT A FILTER.
 *
 * A tool that needs a clicked candle is still MOUNTED without one — disabled, with the reason on
 * hover. `AdminPanelDialog` already does exactly this for SK-Grid and the alternative was
 * considered and rejected there: a control that vanishes when its precondition is unmet is
 * indistinguishable from one the product does not have.
 */
const tool = (id, label, actionId, category, extra = {}) =>
  Object.freeze({ id, label, actionId, category, ...extra });

/** @type {Readonly<Record<string, object>>} */
export const TOOL_REGISTRY = Object.freeze({

  /* ── EPHEMERIS ─────────────────────────────────────────────────────────────────────────── */

  longitude_study: tool('longitude_study', 'Longitude Study', 'open_controls', 'ephemeris'),
  declination_study: tool('declination_study', 'Declination Study', 'open_declination_study', 'ephemeris'),
  degree_range_highlight: tool('degree_range_highlight', 'Degree Range Highlight', 'open_special_studies', 'ephemeris'),
  planetary_lines: tool('planetary_lines', 'Planetary Longitude Lines', 'open_planetary_lines_study', 'ephemeris'),
  average_longitude: tool('average_longitude', 'Average Longitude Lines', 'open_average_longitude_study', 'ephemeris'),
  planet_follow: tool('planet_follow', 'Planet Follow Study', 'open_planet_follow_study', 'ephemeris'),
  area_highlight: tool('area_highlight', 'Area Highlight', 'open_area_highlight_study', 'ephemeris'),
  planet_characteristics: tool('planet_characteristics', 'Planet Characteristics Plot', 'open_planet_characteristics_dialog', 'ephemeris'),
  visual_universe: tool('visual_universe', 'Visual Universe', 'open_visual_universe', 'ephemeris'),
  intraday_animation: tool('intraday_animation', 'Intraday Animation', 'open_intraday_animation', 'ephemeris'),
  astro_catalog_notes: tool('astro_catalog_notes', 'Astro Catalog & Notes', 'open_astro_catalog_notes', 'ephemeris'),

  /* ── CYCLE ─────────────────────────────────────────────────────────────────────────────── */

  cts_scan_1: tool('cts_scan_1', 'CTS 1 — tightest orb', 'cts_scan_1', 'cycle'),
  cts_scan_2: tool('cts_scan_2', 'CTS 2 — middle orb', 'cts_scan_2', 'cycle'),
  cts_scan_3: tool('cts_scan_3', 'CTS 3 — loosest orb', 'cts_scan_3', 'cycle'),
  pta_cycle: tool('pta_cycle', 'PTA — Long Cycle', 'run_cycle_long', 'cycle'),
  ptb_cycle: tool('ptb_cycle', 'PTB — Short Cycle', 'run_cycle_short', 'cycle'),
  engine_pta: tool('engine_pta', 'Run PTA', 'run_engine_pta', 'cycle'),
  engine_ptb: tool('engine_ptb', 'Run PTB', 'run_engine_ptb', 'cycle'),
  engine_cts: tool('engine_cts', 'Run CTS', 'run_engine_cts', 'cycle'),
  trendpulse: tool('trendpulse', 'TrendPulse', 'run_trendpulse', 'cycle'),
  pattern_prediction: tool('pattern_prediction', 'Pattern Prediction', 'open_pattern_prediction_dialog', 'cycle'),
  validation_lines: tool('validation_lines', 'Validation Lines', 'evaluate_validation_lines', 'cycle'),
  spiral_reversal_scan: tool('spiral_reversal_scan', 'Φ Reversal Scan', 'spiral_reversal_scan', 'cycle'),
  fixed_cycles: tool('fixed_cycles', 'Fixed Length Cycles', 'open_fixed_cycle_dialog', 'cycle'),
  global_scan: tool('global_scan', 'Run Global Scan', 'run_global_scan', 'cycle'),
  auto_analysis: tool('auto_analysis', 'Auto-Analysis', 'auto_analysis', 'cycle'),

  /* ── GEOMETRIC ─────────────────────────────────────────────────────────────────────────── */

  sk360: tool('sk360', 'SK360 — 360° price projection', 'toggle_sk360', 'geometric', { requiresLBC: false }),
  time_projection: tool('time_projection', 'Time Projection — price as days', 'toggle_time_projection', 'geometric', { requiresLBC: false }),
  /*
   * ⚠ THE ONLY TOOL THAT REFUSES AN ABSENT ANCHOR. Its two siblings fall back to the last bar;
   * this one returns null, and the builder enforces that as well as the UI — a hidden control is
   * a convenience and not a guard. See `skGrid.js`.
   */
  sk_grid: tool('sk_grid', 'SK-Grid — harmonic price ladder', 'toggle_sk_grid', 'geometric', { requiresLBC: true }),
  recent_pivot_fib: tool('recent_pivot_fib', 'Recent Pivot Fib Projections', 'toggle_recent_fib_projections', 'geometric'),
  pivot_addition_series: tool('pivot_addition_series', 'Pivot Addition Series', 'run_pivot_addition_series', 'geometric'),
  fib_pivot_pairs: tool('fib_pivot_pairs', 'Fibonacci Pivot Pairs', 'run_fib_pivot_pairs', 'geometric'),

  /* ── DRAWING ───────────────────────────────────────────────────────────────────────────── */

  draw_vertical_line: tool('draw_vertical_line', 'Vertical segment', 'draw_vertical_line', 'drawing'),
  draw_horizontal_line: tool('draw_horizontal_line', 'Horizontal segment', 'draw_horizontal_line', 'drawing'),
  draw_trendline: tool('draw_trendline', 'Trendline', 'draw_trendline', 'drawing'),
  draw_extended_trendline: tool('draw_extended_trendline', 'Extended trendline', 'draw_extended_trendline', 'drawing'),
  draw_rectangle: tool('draw_rectangle', 'Rectangle', 'draw_rectangle', 'drawing'),
  draw_text: tool('draw_text', 'Text', 'draw_text', 'drawing'),
  draw_vertical_full: tool('draw_vertical_full', 'Full vertical line', 'draw_vertical_full', 'drawing'),
  draw_circle_empty: tool('draw_circle_empty', 'Empty circle', 'draw_circle_empty', 'drawing'),
  draw_circle_filled: tool('draw_circle_filled', 'Filled circle', 'draw_circle_filled', 'drawing'),
  draw_fib_time: tool('draw_fib_time', 'Fibonacci Time', 'draw_fib_time', 'drawing'),
  draw_fibonacci: tool('draw_fibonacci', 'Fibonacci retracement', 'draw_fibonacci', 'drawing'),
  draw_pitchfork: tool('draw_pitchfork', "Andrews' pitchfork", 'draw_pitchfork', 'drawing'),

  /* ── INDICATOR ─────────────────────────────────────────────────────────────────────────── */

  rsi: tool('rsi', 'RSI', 'toggle_rsi_overlay', 'indicator'),
  macd: tool('macd', 'MACD', 'toggle_macd_overlay', 'indicator'),
  support_resistance: tool('support_resistance', 'Support & Resistance lines', 'open_horizontal_line_dialog', 'indicator'),
  moving_averages: tool('moving_averages', 'Moving averages', 'open_pattern_config_dialog', 'indicator'),

  /* ── UTILITY ───────────────────────────────────────────────────────────────────────────── */

  clear_all: tool('clear_all', 'Clear all (X)', 'clear_all_overlays', 'utility'),
  /*
   * ⚠ X1 DISPATCHES THROUGH A PROP, not the router — `onDeleteLastDrawing`. It is in the registry
   * anyway because the manifests name it, and the manifest decides whether it MOUNTS; how it
   * invokes is a separate question and giving it a dispatcher case is Stage 2 work. `actionId`
   * records the name it would take.
   */
  undo_last: tool('undo_last', 'Undo last drawing (X1)', 'delete_last_drawing', 'utility', { viaProp: 'onDeleteLastDrawing' }),
  chart_type: tool('chart_type', 'Candles / line', 'toggle_chart_type', 'utility', { viaProp: 'onToggleChartType' }),
  area_zoom: tool('area_zoom', 'Area Zoom', 'area_zoom', 'utility', { viaProp: 'onAreaZoom' }),
  watermark_toggle: tool('watermark_toggle', 'Show / hide watermark', 'toggle_watermark', 'utility', { viaProp: 'onToggleWatermark' }),
  save_screenshot: tool('save_screenshot', 'Save screenshot', 'save_screenshot', 'utility'),
  copy_chart: tool('copy_chart', 'Copy chart', 'copy_chart', 'utility'),
  sweep_screenshots: tool('sweep_screenshots', 'Sweep screenshots', 'sweep_screenshots', 'utility'),
  elliott_wave: tool('elliott_wave', 'Elliott Wave Analysis', 'elliott_wave', 'utility', { viaProp: 'onElliottWave' }),
  chart_comparison: tool('chart_comparison', 'Compare two periods', 'open_chart_comparison', 'utility'),
  daily_scan_viewer: tool('daily_scan_viewer', 'Daily Scan Viewer', 'open_daily_scan_viewer', 'utility'),
  /*
   * ⚠ BOTH ARE DISPATCHED NOW, where they were `viaProp` toolbar controls behind a bare
   * `props.isAdmin &&`. They live in the Admin Panel's System tab, and a panel row dispatches
   * through `onAction` — so each needed a real case in `actionDispatcher.js` or it would have
   * rendered a button that did nothing.
   */
  messages_panel: tool('messages_panel', 'Messages panel — study output', 'open_messages_panel', 'utility'),
  /*
   * ⚠ `multi_tab_strategy` WAS HERE AND IS NOT A CONTROL. `MinimalMultiTabDialogTrigger` is the
   * component that renders the ASTRO CATALOG & NOTES button — the name is historical — so the
   * registry carried two ids for one button and the manifest named both. Caught by the
   * renderability test: nothing in the toolbar gates it, because there is nothing to gate.
   */
  cycles_dialog: tool('cycles_dialog', 'Cycles dialog', 'open_cycles_dialog', 'utility', { viaProp: 'onOpenCyclesDialog' }),
  admin_panel: tool('admin_panel', 'Admin Panel', 'open_admin_panel', 'utility'),

  configuration: tool('configuration', 'Configuration — colours', 'open_configuration_dialog', 'utility'),
  message_centre: tool('message_centre', 'Message Centre', 'open_messages_dialog', 'utility'),
  alpha_replay: tool('alpha_replay', 'Alpha Replay', 'toggle_simulate', 'utility'),
  dynaman_simulator: tool('dynaman_simulator', 'DynaMan Simulator', 'open_dynaman_simulator', 'utility'),
  workbench: tool('workbench', 'Verifiable Alpha Workbench', 'launch_simulator', 'utility'),
  chart_analytica: tool('chart_analytica', 'Chart Analytica', 'chart_analytica', 'utility'),
  market_scanner: tool('market_scanner', 'Market Scanner', 'run_market_scanner', 'utility'),
  portfolio_manager: tool('portfolio_manager', 'Portfolio Manager', 'open_portfolio_manager', 'utility'),

  strategy_1: tool('strategy_1', 'Strategy Designer 1', 'open_strategy_design_dialog', 'cycle'),
  strategy_2: tool('strategy_2', 'Strategy Designer 2', 'open_strategy_2_design_dialog', 'cycle'),
  time_event_strategy: tool('time_event_strategy', 'Time and Event Strategy', 'open_time_and_event_strategy_dialog', 'cycle'),
  moa_strategy: tool('moa_strategy', 'MOA Strategy Designer', 'open_moa_strategy_dialog', 'cycle'),
});

/** Every tool id, for validation. */
export const TOOL_IDS = Object.freeze(Object.keys(TOOL_REGISTRY));

/** The categories a tool may declare. */
export const TOOL_CATEGORIES = Object.freeze([
  'ephemeris', 'cycle', 'geometric', 'drawing', 'indicator', 'utility',
]);

/** One tool, or undefined. Never throws — a manifest naming an unknown id is caught by a test. */
export const toolById = (id) => TOOL_REGISTRY[id];

/**
 * The action ids a list of tool ids resolves to.
 *
 * <p>This is the bridge the toolbar uses while the icons still live in `TabPanel`: it maps the
 * manifest's vocabulary onto the one the existing declaration is keyed by. It SKIPS unknown ids
 * rather than throwing — a manifest typo must not blank the toolbar at runtime, and a test
 * asserts every manifest names only ids that exist.
 */
export const actionIdsOf = (toolIds = []) => toolIds
  .map((id) => TOOL_REGISTRY[id])
  .filter(Boolean)
  .map((t) => t.actionId);

/** Whether {@code toolIds} contains a tool whose action is {@code actionId}. */
export const includesAction = (toolIds, actionId) =>
  actionIdsOf(toolIds).includes(actionId);


/**
 * The tool whose dispatcher action is {@code actionId}, or undefined.
 *
 * <p>⚠ BUILT ONCE, not searched per call. The command gate asks this for every command, and a
 * linear scan of seventy-odd entries per keystroke is the kind of cost nobody measures until it
 * is in a loop. A test asserts no two tools share an action, so the map cannot lose an entry.
 */
const BY_ACTION = Object.freeze(Object.fromEntries(
  TOOL_IDS.map((id) => [TOOL_REGISTRY[id].actionId, TOOL_REGISTRY[id]]),
));

export const toolForAction = (actionId) => BY_ACTION[actionId];

/**
 * Whether {@code actionId} is a TOOL at all.
 *
 * <p>⚠ THE LOAD-BEARING DISTINCTION FOR THE COMMAND GATE. A manifest is an allow-list of tools,
 * so asking it about a command that is not a tool — `set_symbol`, a navigation, a drawing-mode
 * change — would refuse everything it has never heard of. Only an action in this map is governed
 * by a manifest; everything else is governed by the gate's other four axes.
 */
export const isToolAction = (actionId) => Boolean(BY_ACTION[actionId]);

/** Tools in a category, in declaration order. Used for grouping, never for gating. */
export const toolsInCategory = (category) =>
  TOOL_IDS.map((id) => TOOL_REGISTRY[id]).filter((t) => t.category === category);
